import { IconButton } from '@plato/ui/candidates/icon-button';
import type { ButtonIntent } from '@plato/ui/primitives/button';
import { InputGroup, InputGroupTextarea } from '@plato/ui/primitives/input-group';
import { cn } from '@plato/ui/util/classes';
import { ArrowUpIcon, SquareIcon } from 'lucide-react';
import type { ComponentPropsWithRef, ReactElement } from 'react';

export type PromptInputProps = ComponentPropsWithRef<'form'>;

export function PromptInput({ className, ...props }: PromptInputProps): ReactElement {
    return <InputGroup className={cn('rounded-default', className)} render={<form {...props} />} />;
}

export type PromptInputTextareaProps = ComponentPropsWithRef<'textarea'>;

export function PromptInputTextarea({
    className,
    onKeyDown,
    placeholder = 'Ask anything',
    ...props
}: PromptInputTextareaProps): ReactElement {
    return (
        <InputGroupTextarea
            className={cn('field-sizing-content max-h-40', className)}
            placeholder={placeholder}
            onKeyDown={(event) => {
                onKeyDown?.(event);
                if (
                    event.key === 'Enter' &&
                    !event.shiftKey &&
                    !event.nativeEvent.isComposing &&
                    !event.defaultPrevented
                ) {
                    event.preventDefault();
                    event.currentTarget.form?.requestSubmit();
                }
            }}
            {...props}
        />
    );
}

export type PromptInputStatus = 'ready' | 'streaming';

export type PromptInputSubmitProps = ComponentPropsWithRef<'button'> & {
    label?: string;
    status?: PromptInputStatus;
    intent?: ButtonIntent;
};

export function PromptInputSubmit({
    className,
    intent,
    label,
    status = 'ready',
    ...props
}: PromptInputSubmitProps): ReactElement {
    return (
        <IconButton
            type="submit"
            variant="primary"
            intent={intent}
            label={label ?? (status === 'streaming' ? 'Stop' : 'Send')}
            className={className}
            {...props}
        >
            {status === 'streaming' ? <SquareIcon className="fill-current" /> : <ArrowUpIcon />}
        </IconButton>
    );
}
